import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { useStorePos } from "./useStorePos";
import { formatearMoneda, monedaInputNumberProps, MONEDA_CERO_TEXTO } from "../../config/moneda";
import { PERMISOS, puedeAcceder } from "../../servicios/sesionSeguridad";

const normalizar = (value) => String(value || "").trim().toLowerCase();

const PosPopupPreciosEditar = ({ visible, onHide }) => {
  const { t } = useTranslation();
  const puedeModificar = puedeAcceder(PERMISOS.PRECIOS_MODIFICAR);
  const ListaPreciosEditable = useStorePos((s) => s.ListaPreciosEditable);
  const CargandoListaPrecios = useStorePos((s) => s.CargandoListaPrecios);
  const GuardandoListaPrecios = useStorePos((s) => s.GuardandoListaPrecios);
  const cargarListaPreciosEditable = useStorePos((s) => s.cargarListaPreciosEditable);
  const setPrecioProductoEditable = useStorePos((s) => s.setPrecioProductoEditable);
  const guardarListaPrecios = useStorePos((s) => s.guardarListaPrecios);
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState("todos");

  useEffect(() => {
    if (visible) {
      cargarListaPreciosEditable();
    } else {
      setBusqueda("");
      setFiltro("todos");
    }
  }, [visible, cargarListaPreciosEditable]);

  const handleGuardar = async () => {
    const guardado = await guardarListaPrecios();
    if (guardado) {
      onHide();
    }
  };

  const resumen = useMemo(() => {
    const total = ListaPreciosEditable.length;
    const conPrecio = ListaPreciosEditable.filter((p) => Number(p.nPrecio) > 0).length;
    const sinPrecio = total - conPrecio;
    const suma = ListaPreciosEditable.reduce((acc, p) => acc + (Number(p.nPrecio) || 0), 0);

    return {
      total,
      conPrecio,
      sinPrecio,
      promedio: conPrecio > 0 ? suma / conPrecio : 0,
    };
  }, [ListaPreciosEditable]);

  const productosFiltrados = useMemo(() => {
    const texto = normalizar(busqueda);

    return ListaPreciosEditable.filter((p) => {
      const precio = Number(p.nPrecio) || 0;
      const coincideFiltro =
        filtro === "todos" ||
        (filtro === "conPrecio" && precio > 0) ||
        (filtro === "sinPrecio" && precio <= 0);
      const coincideTexto =
        !texto ||
        normalizar(p.sDescripcion).includes(texto) ||
        normalizar(p.sCodigo).includes(texto);

      return coincideFiltro && coincideTexto;
    });
  }, [ListaPreciosEditable, busqueda, filtro]);

  const header = (
    <div className="pos-precios-dialog-header">
      <span className="pos-precios-header-icon">
        <i className="pi pi-pencil" />
      </span>
      <div>
        <h3>{t("products.modifyPrices")}</h3>
        <p>{t("products.generalPriceList")}</p>
      </div>
    </div>
  );

  return (
    <Dialog
      header={header}
      position="center"
      visible={visible}
      style={{ width: "min(920px, 94vw)", height: "min(84vh, 780px)" }}
      breakpoints={{ "768px": "96vw", "480px": "98vw" }}
      maximizable={true}
      contentStyle={{ backgroundColor: "white", display: "flex", flexDirection: "column", padding: 0 }}
      closable={true}
      onHide={onHide}
      className="pos-dialog pos-precios-dialog"
    >
      <div className="pos-precios-modal pos-precios-modal-editor">
        <div className="pos-precios-topbar">
          <span className="pos-precios-note pos-precios-note-warning">
            <i className="pi pi-exclamation-circle" />
            {t("products.zeroPriceUnavailable", { amount: MONEDA_CERO_TEXTO })}
          </span>
          <span className="pos-precios-count">
            {t("products.shownOfTotal", { shown: productosFiltrados.length, total: ListaPreciosEditable.length })}
          </span>
        </div>

        <div className="pos-precios-resumen" aria-label={t("products.editPricesSummary")}>
          <div>
            <span>{t("products.products")}</span>
            <strong>{resumen.total}</strong>
          </div>
          <div>
            <span>{t("products.withPrice")}</span>
            <strong>{resumen.conPrecio}</strong>
          </div>
          <div>
            <span>{t("products.withoutPrice")}</span>
            <strong>{resumen.sinPrecio}</strong>
          </div>
          <div>
            <span>{t("products.averagePrice")}</span>
            <strong>{formatearMoneda(resumen.promedio)}</strong>
          </div>
        </div>

        <div className="pos-precios-toolbar">
          <label className="pos-precios-search">
            <i className="pi pi-search" />
            <span className="pos-sr-only">{t("products.searchByCodeOrDescription")}</span>
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={t("products.searchByCodeOrDescription")}
            />
          </label>

          <div className="pos-precios-filtros" role="group" aria-label={t("products.editFilters")}>
            <button
              type="button"
              className={filtro === "todos" ? "activo" : ""}
              aria-pressed={filtro === "todos"}
              onClick={() => setFiltro("todos")}
            >
              {t("common.all")}
            </button>
            <button
              type="button"
              className={filtro === "conPrecio" ? "activo" : ""}
              aria-pressed={filtro === "conPrecio"}
              onClick={() => setFiltro("conPrecio")}
            >
              {t("products.withPrice")}
            </button>
            <button
              type="button"
              className={filtro === "sinPrecio" ? "activo" : ""}
              aria-pressed={filtro === "sinPrecio"}
              onClick={() => setFiltro("sinPrecio")}
            >
              {t("products.withoutPrice")}
            </button>
          </div>
        </div>

        <div className="pos-precios-tabla-wrap">
          {CargandoListaPrecios ? (
            <div className="pos-precios-state">
              <i className="pi pi-spin pi-spinner" />
              <strong>{t("products.loadingProducts")}</strong>
            </div>
          ) : ListaPreciosEditable.length === 0 ? (
            <div className="pos-precios-state">
              <i className="pi pi-inbox" />
              <strong>{t("products.noProductsAvailable")}</strong>
            </div>
          ) : productosFiltrados.length === 0 ? (
            <div className="pos-precios-state">
              <i className="pi pi-search" />
              <strong>{t("products.noProductsToShow")}</strong>
              <span>{t("products.reviewSearchOrFilter")}</span>
            </div>
          ) : (
            <table className="pos-precios-tabla">
              <thead>
                <tr>
                  <th>{t("pos.product")}</th>
                  <th className="pos-precios-col-precio">{t("common.price")}</th>
                </tr>
              </thead>
              <tbody>
                {productosFiltrados.map((p) => {
                  const sinPrecio = Number(p.nPrecio) <= 0;
                  return (
                    <tr key={p.Id} className={sinPrecio ? "sin-precio" : ""}>
                      <td data-label={t("pos.product")}>
                        <div className="pos-precios-producto-cell">
                          <strong>{p.sDescripcion}</strong>
                          {p.sCodigo && <span>{t("products.codeLabel", { code: p.sCodigo })}</span>}
                        </div>
                      </td>
                      <td data-label={t("products.priceWithoutVat")} className="pos-precios-col-precio">
                        <InputNumber
                          aria-label={t("products.priceWithoutVatFor", { product: p.sDescripcion || p.sCodigo || t("pos.product") })}
                          value={Number(p.nPrecio) || 0}
                          onValueChange={(e) => setPrecioProductoEditable(p.Id, e.value ?? 0)}
                          {...monedaInputNumberProps}
                          min={0}
                          disabled={!puedeModificar}
                          inputClassName="pos-precios-input"
                          className="pos-precios-inputnumber"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="pos-precios-footer">
          <Button
            label={t("common.cancel")}
            icon="pi pi-times"
            className="pos-btn-secondary"
            onClick={onHide}
            disabled={GuardandoListaPrecios}
          />
          <Button
            label={t("customers.saveChanges")}
            icon="pi pi-check"
            className="pos-btn-confirmar"
            onClick={handleGuardar}
            loading={GuardandoListaPrecios}
            disabled={!puedeModificar || CargandoListaPrecios || ListaPreciosEditable.length === 0}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default PosPopupPreciosEditar;
